import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

export const SERVICES = {
  individual: {
    name: "Individual Tax Filing",
    tagline: "Federal + state 1040s prepared by licensed professionals.",
    audience: ["W-2 employees", "Freelancers & 1099 earners", "Multi-state filers", "RSU/ISO/ESPP holders"],
    deliverables: ["Federal Form 1040 & 1040-NR", "Each state return (+$19.99)", "Schedules A, B, C, D, E (+$49.99 ea)", "Estimated tax vouchers"],
    pricing: "Starting at $29.99",
  },
  business: {
    name: "Business Tax Filing",
    tagline: "1120, 1120-S and 1065 with QBI and depreciation strategy.",
    audience: ["C-Corps", "S-Corps", "Partnerships", "Multi-member LLCs"],
    deliverables: ["Entity federal return", "State franchise filings", "K-1s to owners", "Depreciation schedules"],
    pricing: "Starting at $899",
  },
  itin: {
    name: "ITIN Application",
    tagline: "Form W-7 preparation with CAA-guided document authentication.",
    audience: ["Non-resident spouses", "Dependents on returns", "Foreign investors in US assets"],
    deliverables: ["Prepared Form W-7", "Document checklist", "Certifying Acceptance Agent referral", "IRS status monitoring"],
    pricing: "Starting at $199.99",
  },
  "fbar-fatca": {
    name: "FBAR & FATCA",
    tagline: "FinCEN 114 and Form 8938 for US persons with foreign accounts.",
    audience: ["US citizens abroad", "Green card holders", "Dual nationals", "Owners of foreign entities"],
    deliverables: ["FinCEN 114 (FBAR)", "Form 8938 (FATCA)", "Streamlined disclosure guidance", "Foreign entity classification"],
    pricing: "Starting at $149",
  },
  "irs-notice": {
    name: "IRS Notice Assistance",
    tagline: "CP2000, CP14, LT11 and audit correspondence — resolved fast.",
    audience: ["Individuals receiving IRS letters", "Small businesses under examination", "CPAs delegating notice work"],
    deliverables: ["Notice analysis", "Drafted response letter", "Power of Attorney (Form 2848)", "Payment plan negotiation"],
    pricing: "$249 / notice",
  },
  extensions: {
    name: "Tax Extensions",
    tagline: "Form 4868 & 7004 filed same-day with payment vouchers.",
    audience: ["Individuals needing more time", "Businesses awaiting K-1s", "International filers"],
    deliverables: ["Federal extension", "State extensions", "Estimated payment voucher", "Confirmation of receipt"],
    pricing: "$49 flat",
  },
  corporate: {
    name: "Corporate Programs",
    tagline: "Managed tax operations for CPA firms, EAs and enterprise finance.",
    audience: ["CPA firms", "Enrolled Agent practices", "Enterprise finance teams", "Family offices"],
    deliverables: ["Dedicated engagement partner", "White-label client portal", "SLA-backed notice response", "Custom analytics reports"],
    pricing: "Custom quote",
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES[slug];
  if (!service) return <Navigate to="/services" replace />;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="border-b tf-hairline bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-20">
          <Link to="/services" className="text-sm text-slate-500 hover:text-[#0A2540]" data-testid="service-back">← All services</Link>
          <div className="mt-4 grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <Badge className="bg-[#D32F2F]/10 text-[#D32F2F] hover:bg-[#D32F2F]/15">Service</Badge>
              <h1 className="mt-4 font-[Chivo] text-5xl lg:text-6xl font-black text-[#0A2540] tracking-tight" data-testid="service-title">{service.name}</h1>
              <p className="mt-4 text-lg text-slate-600 max-w-2xl">{service.tagline}</p>
            </div>
            <div className="rounded-md border tf-hairline p-6 bg-slate-50">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Pricing</div>
              <div className="mt-2 font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="service-price">{service.pricing}</div>
              <Link to="/register"><Button className="mt-4 w-full bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="service-cta">Start now <ArrowRight size={14} className="ml-2" /></Button></Link>
              <Link to="/contact"><Button variant="outline" className="mt-2 w-full border-[#0A2540] text-[#0A2540]">Talk to an advisor</Button></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-[Chivo] text-3xl font-bold text-[#0A2540]">Who this is for</h2>
          <ul className="mt-6 space-y-3">
            {service.audience.map((a) => (
              <li key={a} className="flex items-start gap-2 text-slate-700"><CheckCircle size={18} weight="fill" className="text-[#0A2540] mt-0.5" />{a}</li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="font-[Chivo] text-3xl font-bold text-[#0A2540]">What you receive</h2>
          <ul className="mt-6 space-y-3">
            {service.deliverables.map((d) => (
              <li key={d} className="flex items-start gap-2 text-slate-700"><CheckCircle size={18} weight="fill" className="text-[#D32F2F] mt-0.5" />{d}</li>
            ))}
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
}
